import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Download, Search, X, Camera, Rows3, Rows2 } from "lucide-react";
import { exportCsvUrl } from "@/lib/api";
import MultiSelect from "@/components/MultiSelect";

export function InventoryHeader({ loading, total, onAdd }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Catalogue</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none">Inventory</h1>
        <div className="text-sm text-[#3A2A10] mt-3 font-mono-data">
          {loading ? "…" : `${total.toLocaleString()} pieces`}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={exportCsvUrl()}
          className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-wider border border-[#8B6A1F]/45 hover:border-[#8B6A1F]/60 text-[#0A0604] transition-colors"
          data-testid="export-csv-link"
        >
          <Download size={14} /> Export
        </a>
        <Button
          onClick={onAdd}
          className="h-9 px-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm"
          data-testid="add-watch-button"
        >
          <Plus size={14} className="mr-1.5" /> Add Watch
        </Button>
      </div>
    </header>
  );
}

function FilterSelect({ value, onChange, options = [], placeholder, testid, widthClass = "" }) {
  return (
    <Select value={value || "__all__"} onValueChange={onChange}>
      <SelectTrigger
        className={`h-10 bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus:ring-[#D4AF37]/40 ${widthClass}`}
        data-testid={testid}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm combobox-scroll"
        style={{
          maxHeight: "min(320px, var(--radix-select-content-available-height, 320px))",
        }}
      >
        <SelectItem value="__all__">All {placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={String(o)} value={String(o)}>{String(o)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function InventoryFilterBar({
  qLive, onSearchChange,
  filters, onFilterChange,
  onClear,
  activeFilterCount,
  validation,
  hideSold, onHideSoldChange,
  archiveMode, onArchiveModeChange,
  showcaseReady, onShowcaseReadyChange,
  density, onDensityChange,
}) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
          <Input
            value={qLive}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search ID, brand, model, caliber, SN, eBay ID…"
            className="pl-9 h-10 bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus-visible:ring-[#D4AF37]/40"
            data-testid="search-input"
          />
        </div>
        <MultiSelect
          value={filters.status || []}
          onChange={(v) => onFilterChange("status", v)}
          options={validation["Status"] || []}
          placeholder="All Statuses"
          testid="filter-status"
        />
        <MultiSelect
          value={filters.caliber || []}
          onChange={(v) => onFilterChange("caliber", v)}
          options={validation["Caliber"] || []}
          placeholder="All Calibers"
          testid="filter-caliber"
        />
        <FilterSelect value={filters.movement_type} onChange={(v) => onFilterChange("movement_type", v)} options={validation["Movement Type"]} placeholder="Movement" testid="filter-movement" />
      </div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <FilterSelect value={filters.brand} onChange={(v) => onFilterChange("brand", v)} options={validation["Maker"]} placeholder="Brand" testid="filter-brand" widthClass="w-52" />
        <FilterSelect value={filters.bin_location} onChange={(v) => onFilterChange("bin_location", v)} options={validation["Location"]} placeholder="Location" testid="filter-drawer" widthClass="w-40" />
        <label
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#3A2A10] cursor-pointer select-none"
          data-testid="hide-sold-toggle"
        >
          <input
            type="checkbox"
            checked={hideSold}
            onChange={(e) => onHideSoldChange(e.target.checked)}
            className="w-4 h-4 accent-[#D4AF37]"
          />
          Hide SOLD
        </label>
        {/* Archive visibility — three-state radio-ish, rendered as a compact
            inline segmented control. `hide` is the default; `show` reveals
            archived rows alongside active (with an Archived badge); `only`
            switches to an archive-only view. */}
        <div
          className="inline-flex items-center gap-0.5 border border-[#8B6A1F]/45 rounded-sm overflow-hidden"
          data-testid="archive-mode-toggle"
        >
          {[
            { v: "hide", label: "Active" },
            { v: "show", label: "+ Archived" },
            { v: "only", label: "Archive" },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => onArchiveModeChange(o.v)}
              className={`px-2.5 h-[34px] text-[10px] uppercase tracking-wider transition-colors ${archiveMode === o.v ? "bg-[#D4AF37] text-[#0A0C10]" : "text-[#3A2A10] hover:bg-[#2B2214]/[0.08]"}`}
              data-testid={`archive-mode-${o.v}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {/* Showcase Ready — quick toggle to surface watches with both
            BEFORE and AFTER pics, ready to share to social/eBay/Etsy. */}
        <button
          type="button"
          onClick={() => onShowcaseReadyChange(!showcaseReady)}
          className={`inline-flex items-center gap-1.5 px-2.5 h-[34px] text-[10px] uppercase tracking-wider border rounded-sm transition-colors ${
            showcaseReady
              ? "bg-[#D4AF37] text-[#0A0C10] border-[#D4AF37]"
              : "bg-transparent text-[#3A2A10] border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.08]"
          }`}
          data-testid="filter-showcase-ready"
          title="Show only watches with both before & after photos"
        >
          <Camera size={12} /> Showcase Ready
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={onClear}
            className="inline-flex items-center gap-1.5 text-xs text-[#3A2A10] transition-colors"
            data-testid="clear-filters"
          >
            <X size={12} /> Clear ({activeFilterCount})
          </button>
        )}
        {/* Density toggle — Mac/PC only (desktop table has variable density,
            mobile cards are one-size). Persists to localStorage. */}
        {onDensityChange && (
          <div
            className="hidden md:inline-flex items-center gap-0.5 border border-[#8B6A1F]/45 rounded-sm overflow-hidden ml-auto"
            data-testid="density-toggle"
            title="Row density"
          >
            <button
              type="button"
              onClick={() => onDensityChange("comfortable")}
              className={`px-2 h-[34px] inline-flex items-center gap-1 text-[10px] uppercase tracking-wider transition-colors ${density === "comfortable" ? "bg-[#D4AF37] text-[#0A0C10]" : "text-[#3A2A10] hover:bg-[#2B2214]/[0.08]"}`}
              data-testid="density-comfortable"
              aria-label="Comfortable row density"
            >
              <Rows2 size={13} />
            </button>
            <button
              type="button"
              onClick={() => onDensityChange("compact")}
              className={`px-2 h-[34px] inline-flex items-center gap-1 text-[10px] uppercase tracking-wider transition-colors ${density === "compact" ? "bg-[#D4AF37] text-[#0A0C10]" : "text-[#3A2A10] hover:bg-[#2B2214]/[0.08]"}`}
              data-testid="density-compact"
              aria-label="Compact row density"
            >
              <Rows3 size={13} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
